import React, { useState, useEffect } from 'react';
import {
  InputAdornment,
  IconButton,
  Link,
  ThemeProvider,
  Box,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Login } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { GoogleLogin } from '@react-oauth/google';
import { signIn as signInApi } from '../DataService';
import { saveClaims, saveUserName, saveQueryParamsToCookies } from '../CookieHelper';
import { theme } from '../styles/theme/theme';
import {
  AuthPageContainer,
  AuthFormCard,
  AuthFormContainer,
  AuthNavContainer,
  AuthPageTitle,
  AuthSubtitle,
  AuthErrorMessage,
  AuthTextField,
  AuthGradientButton,
  AuthCheckbox,
  AuthFormControlLabel,
} from '../styles/components';

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: Cookies.get('username') || '',
    password: '',
    save: Cookies.get('save') === 'true',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location.search) {
      saveQueryParamsToCookies(location.search);
      const urlParam = new URLSearchParams(location.search).get('url');
      if (urlParam) localStorage.setItem('url', urlParam);
    }
  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const redirectClient = (res: any) => {
    setLoading(true);
    let accountUrl = '';
    const urlParam = new URLSearchParams(location.search).get('url');
    const url = urlParam || localStorage.getItem('url');
    if (urlParam) {
      accountUrl = decodeURIComponent(urlParam);
    } else if (url) {
      accountUrl = decodeURIComponent(url);
    } else {
      accountUrl = res.RedirectUrl;
    }
    if (accountUrl.indexOf('uid') === -1) {
      if (accountUrl.indexOf('?') > 0) {
        accountUrl += `&uid=${res.Claims.UserId}`;
      } else {
        accountUrl += `?uid=${res.Claims.UserId}`;
      }
    }
    localStorage.removeItem('url');
    window.location.href = accountUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await signInApi({
        email: formData.email,
        password: formData.password,
        save: formData.save,
      });
      saveClaims(res.Claims);
      saveUserName(formData);
      redirectClient(res);
    } catch (err: any) {
      setError(err?.message || 'Sign in failed.');
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthPageContainer>
        <AuthFormCard elevation={6}>
          <AuthPageTitle variant="h5">
            Sign In
          </AuthPageTitle>
          
          <AuthSubtitle variant="body2">
            Sign in to continue to your account
          </AuthSubtitle>

          <Box component="form" onSubmit={handleSubmit}>
            <AuthFormContainer>
              <AuthTextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
              
              <AuthTextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <AuthFormControlLabel
                control={
                  <AuthCheckbox 
                    checked={formData.save} 
                    onChange={handleChange} 
                    name="save" 
                    color="primary" 
                  />
                }
                label="Remember me"
              />

              {error && (
                <AuthErrorMessage>
                  {error}
                </AuthErrorMessage>
              )}

              <AuthGradientButton
                type="submit"
                fullWidth
                variant="contained"
                startIcon={<Login />}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Sign In'}
              </AuthGradientButton>

              <Divider sx={{ my: 2, width: '100%' }}>OR</Divider>

              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <GoogleLogin
                  onSuccess={(credentialResponse) => {
                    if (credentialResponse.credential) {
                      navigate(`/google-callback?id_token=${credentialResponse.credential}&flow=signin`);
                    }
                  }}
                  onError={() => {
                    setError('Google login failed. Please try again.');
                  }}
                />
              </Box>

              <AuthNavContainer>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  Forgot password?
                </Link>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/sign-up')}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  Don't have an account? Sign Up
                </Link>
              </AuthNavContainer>
            </AuthFormContainer>
          </Box>
        </AuthFormCard>
      </AuthPageContainer>
    </ThemeProvider>
  );
};

export default SignIn; 